// =========================================================================
// Shared helper for unwrapping a `supabase().functions.invoke()` failure
// into the operator-facing error message the Edge Function actually
// returned.
//
// Why this exists: `@supabase/functions-js` v2 throws
// `FunctionsHttpError(response)` on any non-2xx, where `error.context` is
// the raw `Response` object itself (NOT `{ response: Response }` as older
// docs/blog posts imply). If you reach for `.context.response` you will
// always get `undefined`, your operator will see the generic "Edge
// Function returned a non-2xx status code", and you will lose the body
// message that the function deliberately included for them.
//
// All edge-function wrappers in this package should use
// `unwrapFunctionsInvokeError` instead of hand-rolling this dance, so we
// only have to track upstream's shape in one place.
// =========================================================================

/**
 * Extract the best operator-facing message from a
 * `supabase().functions.invoke()` error.
 *
 * Tries, in order:
 *
 *   1. JSON body of the underlying `Response`, looking for `.error`,
 *      `.message`, or `.msg`.
 *   2. Plain-text body of the response if it isn't JSON.
 *   3. `error.message` from the thrown `FunctionsError`.
 *   4. `fallback` (default: `'Edge function call failed.'`).
 *
 * Never throws. Safe to call from a `catch` block.
 */
export async function unwrapFunctionsInvokeError(
  error: unknown,
  fallback = 'Edge function call failed.',
): Promise<string> {
  const fallbackMsg = (error as { message?: string } | null)?.message ?? fallback;
  const resp = extractResponse(error);
  if (!resp) return fallbackMsg;
  let text = '';
  try {
    text = await resp.clone().text();
  } catch {
    return fallbackMsg;
  }
  if (!text) return fallbackMsg;
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    const msg =
      pickString(parsed.error) ??
      pickString(parsed.message) ??
      pickString(parsed.msg);
    if (msg) return msg;
  } catch {
    // Body wasn't JSON; fall through and return the raw text.
  }
  return text.slice(0, 500); // hard cap so a runaway HTML page can't blow up a toast
}

// -------------------------------------------------------------------------
// Internals
// -------------------------------------------------------------------------

/**
 * `FunctionsHttpError.context` is the `Response` directly. Older code in
 * this repo reached for `.context.response`; that's the historical shape
 * from a pre-2.x SDK and never matched the runtime. We accept either to
 * survive any future churn.
 */
function extractResponse(error: unknown): Response | null {
  if (!error || typeof error !== 'object') return null;
  const ctx = (error as { context?: unknown }).context;
  if (looksLikeResponse(ctx)) return ctx as Response;
  if (ctx && typeof ctx === 'object') {
    const nested = (ctx as { response?: unknown }).response;
    if (looksLikeResponse(nested)) return nested as Response;
  }
  // Some failure paths attach `.response` directly to the FunctionsResponse
  // result alongside `.error`; if a caller passed that to us, honour it.
  const direct = (error as { response?: unknown }).response;
  if (looksLikeResponse(direct)) return direct as Response;
  return null;
}

function looksLikeResponse(value: unknown): value is Response {
  return Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as { clone?: unknown }).clone === 'function' &&
      typeof (value as { text?: unknown }).text === 'function',
  );
}

function pickString(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}
